import { useState, useEffect } from "react";
import { AppBar, Toolbar, IconButton, Box, Menu, MenuItem, Typography, Button, Divider, Avatar } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useCashRegister } from "../../context/CashRegisterContext";
import CategoryManagerModal from "../../pages/location/sales/categories_view/CategoryManagerModal";
import CashRegisterMovementModal from "../../pages/location/sales/cash_register/CashRegisterMovementModal";
import { getLogo } from "../../services/LogoStorageService";
import { getProfile } from "../../services/UserService";
import NotificationsDropdown from "../Notifications/NotificationsDropdown";
import WebOrderAcknowledgementModal from "../Notifications/WebOrderAcknowledgementModal";
import { canViewLearningCenter } from '../../utils/learningAccess';
import PlatformBridge from "../../utils/PlatformBridge";
import { confirmLeaveTablesEditor } from "../../pages/tables/editorGuard";

const Topbar = ({ isCollapsed, onToggleSidebar, isMobile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [logo, setLogo] = useState(null);
  const [currentUser, setCurrentUser] = useState(() => ({
    firstName: "",
    lastName: "",
    email: localStorage.getItem("userEmail") || "",
    profilePictureUrl: "",
  }));
  const empresaId = parseInt(localStorage.getItem("empresaId"), 10);

  useEffect(() => {
    const email = localStorage.getItem("userEmail") || "";
    setCurrentUser((user) => ({ ...user, email }));

    let active = true;
    getProfile()
      .then((profile) => {
        if (!active) return;
        setCurrentUser({
          firstName: profile?.firstName || "",
          lastName: profile?.lastName || "",
          email: profile?.email || email,
          profilePictureUrl: profile?.profilePictureUrl || "",
        });
      })
      .catch((error) => console.error("Error loading current user profile:", error));

    return () => {
      active = false;
    };
  }, []);

  // Cargar logo desde almacenamiento local o descargarlo si no existe
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const token = await PlatformBridge.getToken("accessToken");

        if (!token || !empresaId) {
          // Si no hay token o empresaId, no mostrar logo
          setLogo(null);
          return;
        }
        
        // Obtener logo (primero local, si no existe descargar y guardar)
        const logoUrl = await getLogo(empresaId);
        setLogo(logoUrl);
      } catch (error) {
        // Solo mostrar error si no es 404 (que es esperado cuando no hay logo)
        if (error.message && !error.message.includes('404')) {
          console.error('Error loading logo:', error);
        }
        // En caso de error, no mostrar logo
        setLogo(null);
      }
    };

    loadLogo();
    
    // Cleanup: liberar URL de blob cuando el componente se desmonte o cambie el logo
    return () => {
      if (logo && typeof logo === 'string' && logo.startsWith('blob:')) {
        URL.revokeObjectURL(logo);
      }
    };
  }, [empresaId]); // Recargar cuando cambie el empresaId

  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  // Nuevo estado para el menú de "Actions"
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const { cashStatus, checkCashStatus, setCloseCashRegisterModal } = useCashRegister();

  // 🔍 Extraer `locationId` desde la URL usando una expresión regular
  const match = location.pathname.match(/\/(?:sales|tables)\/(\d+)/);
  const locationId = match ? match[1] : null;

  const isSalesPage = location.pathname.startsWith("/sales/");
  // Mesas también opera con la caja de la terminal: muestra el mismo indicador, pero sin las acciones propias del POS.
  const isTablesPage = location.pathname.startsWith("/tables/");
  const isCashOpen = (isSalesPage || isTablesPage) && locationId && cashStatus[locationId];
  const showCashStatus = locationId && (isSalesPage || (isTablesPage && cashStatus[locationId] !== undefined));
  // Acciones y Cerrar caja son de la caja, así que están en Ventas y en Mesas; lo propio de cada pantalla cambia dentro
  // del menú: categorías de visualización en Ventas, "Editar plano" en Mesas (solo con permiso de editar el plano).
  const canEditTablesLayout = (() => {
    try { return JSON.parse(localStorage.getItem('permissions') || '[]').map(Number).includes(1801); } catch (_) { return false; }
  })();
  const showTablesActions = Boolean(isTablesPage && locationId && (canEditTablesLayout || isCashOpen));
  const inTablesEditor = isTablesPage && new URLSearchParams(location.search).get("mode") === "editor";

  const [viewCategoriesModalOpen, setViewCategoriesModalOpen] = useState(false);
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const canViewPayments = (() => {
    try { return JSON.parse(localStorage.getItem('permissions') || '[]').map(Number).includes(1319); } catch (_) { return false; }
  })();
  const canViewLearning = (() => {
    let permissions = [];
    try { permissions = JSON.parse(localStorage.getItem('permissions') || '[]'); } catch (_) { permissions = []; }
    return canViewLearningCenter({ permissions });
  })();

  // ✅ Asegurar que se obtiene el estado correcto al entrar a Sales
  useEffect(() => {
    if (isSalesPage && locationId && !cashStatus[locationId]) {
      checkCashStatus(locationId, localStorage.getItem("terminalId"));
    }
  }, [isSalesPage, locationId]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Manejo del menú desplegable para "Actions"
  const handleActionsMenuOpen = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsAnchorEl(null);
  };
  
  const handleView = () => {
    setViewCategoriesModalOpen(true);
    handleActionsMenuClose();
  };

  const handleToggleTablesEditor = () => {
    handleActionsMenuClose();
    if (inTablesEditor && !confirmLeaveTablesEditor()) return;
    navigate(inTablesEditor ? `/tables/${locationId}` : `/tables/${locationId}?mode=editor`);
  };

  const handleOpenCloseViewModal = () => {
    setCloseCashRegisterModal(true)
  };

  const handleActionViewModal = () => {
    setViewCategoriesModalOpen(false)
    window.location.reload();
  };

  const currentUserName = [currentUser.firstName, currentUser.lastName]
    .filter(Boolean)
    .join(" ") || "Usuario";

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "white",
        borderBottom: "1px solid #dcdcdc",
        boxShadow: "none",
        zIndex: 1201,
      }}
    >
      <Toolbar className="toolbar-main" sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "6px !important" }}>
        {/* 🔹 Sección Izquierda (Botón de colapso + Logo) */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={onToggleSidebar} sx={{ color: "black" }}>
            <ion-icon
              name={isMobile ? "menu-outline" : isCollapsed ? "chevron-forward-outline" : "chevron-back-outline"}
              style={{ fontSize: "24px" }}
            ></ion-icon>
          </IconButton>
          {logo && <img className="topbar-logo" src={logo} alt="Logo" />}
        </Box>

        {/* 🔹 Estado de la caja */}
        {showCashStatus && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1, justifyContent: "center" }}>
            {/* ✅ Indicador de estado de caja */}
            <Box className={`cash-status ${isCashOpen ? "open" : "closed"}`}>
              {isCashOpen ? (
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 22, color: "green" }} />
              ) : (
                <LockOutlinedIcon sx={{ fontSize: 22, color: "red" }} />
              )}
              <Typography sx={{ fontWeight: "bold", color: isCashOpen ? "green" : "red" }}>
                {isCashOpen ? t("cash_register_open") : t("cash_register_closed")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* 🔹 Botones de Actions y Cerrar Caja */}
        {(isSalesPage || showTablesActions) && locationId && (
          // El espacio hasta la sección derecha es siempre el mismo (20px), con o sin "Cerrar caja": los márgenes propios de
          // cada botón se anulan y lo define este contenedor.
          <Box sx={{ display: "flex", alignItems: "center", gap: 0, mr: "20px", "& .close-cash-button": { marginRight: "0 !important" } }}>
            {/* Botón de Actions con menú desplegable */}
            <Box
              className="close-cash-button"
              sx={{
                height: "64px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "0 !important",
                borderLeft: "1px solid #dcdcdc",
                borderRight: "1px solid #dcdcdc",
                backgroundColor: "#f5f5f5",
                transition: "background 0.3s ease",
                ":hover": { backgroundColor: "#e0e0e0" },
              }}
            >
              <Button
                variant="text"
                color="secondary"
                onClick={handleActionsMenuOpen}
                sx={{
                  height: "100% !important",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                {t("actions")}
              </Button>
            </Box>

            {/* Botón de Cerrar Caja */}
            {(isSalesPage || isTablesPage) && isCashOpen && (
              <Box
                className="close-cash-button"
                sx={{
                  height: "64px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeft: "1px solid #dcdcdc",
                  borderRight: "1px solid #dcdcdc",
                  backgroundColor: "#f5f5f5",
                  transition: "background 0.3s ease",
                  ":hover": { backgroundColor: "#e0e0e0" },
                }}
              >
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleOpenCloseViewModal}
                  sx={{
                    height: "100% !important",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("close_cash_register")}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* 🔹 Sección derecha (Notificaciones + Menú de usuario) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Icono de notificaciones */}
          <NotificationsDropdown />
          <WebOrderAcknowledgementModal />
          
          {/* Menú de usuario */}
          <IconButton
            color="primary"
            onClick={handleMenuOpen}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              flex: '0 0 46px',
              '&:hover': { borderRadius: '50%' }
            }}
          >
            <Avatar
              src={currentUser.profilePictureUrl}
              sx={{ width: 37, height: 37, bgcolor: "transparent", color: "primary.main" }}
            >
              <ion-icon name="person-circle-outline" style={{ fontSize: "37px" }}></ion-icon>
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} sx={{ mt: "30px" }}>
            <Box sx={{ px: 2, py: 1.25, minWidth: 260, display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar src={currentUser.profilePictureUrl} sx={{ width: 42, height: 42 }}>
                <ion-icon name="person-outline" style={{ fontSize: "22px" }}></ion-icon>
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {currentUserName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {currentUser.email}
                </Typography>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); navigate("/profile"); }}>{t("profile")}</MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate("/support"); }}>Soporte</MenuItem>
            {canViewLearning && (
              <MenuItem onClick={() => { handleMenuClose(); navigate("/learning"); }}>Aprendizaje</MenuItem>
            )}
            {canViewPayments && (
              <MenuItem onClick={() => { handleMenuClose(); navigate('/payments-benefits'); }}>Pagos y beneficios</MenuItem>
            )}
            <MenuItem onClick={() => { handleMenuClose(); navigate("/logout"); }}>{t("logout")}</MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* 🔹 Menú desplegable para el botón de Actions */}
      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleActionsMenuClose}
        sx={{ mt: "20px" }}
      >
        {isTablesPage && canEditTablesLayout && (
          <MenuItem onClick={handleToggleTablesEditor}>{inTablesEditor ? "Volver al servicio" : "Editar plano"}</MenuItem>
        )}
        {!isTablesPage && <MenuItem onClick={handleView}>{t("visualization_category")}</MenuItem>}
        {isCashOpen && (
          <MenuItem
            onClick={() => {
              setMovementModalOpen(true);
              handleActionsMenuClose();
            }}
          >
            {t("add_cash_movement")}
          </MenuItem>
        )}
      </Menu>

      {viewCategoriesModalOpen && (
        <CategoryManagerModal 
          locationId={locationId}
          open={viewCategoriesModalOpen} 
          onClose={handleActionViewModal}
        />
      )}

      {movementModalOpen && (
        <CashRegisterMovementModal
          open={movementModalOpen}
          onClose={() => setMovementModalOpen(false)}
          locationId={locationId}
          terminalId={localStorage.getItem("terminalId")}
          onSuccess={() => {
            // Podés hacer algo después del éxito si querés
          }}
        />
      )}

    </AppBar>
  );
};

export default Topbar;
